import {
  Calendar,
  Car,
  ChevronLeft,
  Crown,
  Flag,
  GripVertical,
  Medal,
  Pencil,
  Trash2,
  Trophy,
  Undo2,
  UserX,
} from "lucide-solid";
import {
  For,
  Match,
  Show,
  Switch,
  children,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import {
  DragDropProvider,
  SortableProvider,
  createSortable,
  closestCenter,
  transformStyle,
  useDragDropContext,
  type DragEventHandler,
} from "@thisbeyond/solid-dnd";
import { TournamentFormFields } from "~/components/tournament-form-fields";
import { GamertagFull } from "~/components/gamertag";
import { PageContainer } from "~/components/layout/page";
import {
  PointsScaleHelp,
  PromotionModeHelp,
} from "~/components/promotion-mode-help";
import { PromotionPreview } from "~/components/promotion-preview";
import { RulesDialog } from "~/components/rules-dialog";
import { TournamentCard } from "~/components/tournament-card";
import { Avatar } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogTrigger,
} from "~/components/ui/dialog";
import { EligibleMembersDialog } from "~/components/eligible-members-dialog";
import { playersApi, tournamentsApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { requireFeature } from "~/lib/features";
import { isTournamentEligible } from "~/lib/profile-utils";
import { createTrackList, normalizeMultiplier, tracksFromArrays, tracksToArrays } from "~/lib/tracks";
import { cn, formatDateTime } from "~/lib/utils";

import {
  PROMOTION_MODE_LABELS,
  type CarClass,
  type PromotionMode,
  type RaceType,
  type Tier,
  type Tournament,
  type TournamentStandings,
  type TournamentStatus,
} from "system";
import {
  createMutation,
  createQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import { Link, createFileRoute } from "@tanstack/solid-router";
import { QueryKey } from "~/lib/query-keys";

export const Route = createFileRoute("/_authed/tournaments/$tournamentId")({
  beforeLoad: () => requireFeature("tournaments"),
  component: TournamentDetailPage,
});

const STATUS_STYLES = {
  upcoming: "bg-surface-3 text-text-muted border-border",
  active: "bg-brand/10 text-brand border-brand/20",
  completed: "bg-green-500/10 text-green-400 border-green-500/20",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
} satisfies Record<TournamentStatus, string>;

function raceName(
  trackNames: string[] | null | undefined,
  raceNumber: number,
): string {
  return trackNames?.[raceNumber - 1]?.trim() || `Race ${raceNumber}`;
}

// The enforced car type for a race, or "" when none is set. Aligned to race
// numbers the same way trackNames is (entry i ↔ race i+1).
function carTypeFor(
  carTypes: string[] | null | undefined,
  raceNumber: number,
): string {
  return carTypes?.[raceNumber - 1]?.trim() || "";
}

// The points multiplier for a race (1 = counts once), aligned like trackNames.
function multiplierFor(
  raceMultipliers: number[] | null | undefined,
  raceNumber: number,
): number {
  return normalizeMultiplier(raceMultipliers?.[raceNumber - 1]);
}

// Badge marking a race whose points count more than once. Rendered only for
// multipliers above 1 (callers gate), so plain races carry no extra chrome.
function MultiplierBadge(props: { multiplier: number; class?: string }) {
  return (
    <span
      class={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded border border-gold/30 bg-gold/10 text-label-xs font-bold text-gold whitespace-nowrap",
        props.class,
      )}
      title={`Points count ×${props.multiplier} in this race`}
    >
      ×{props.multiplier} points
    </span>
  );
}

function CarTypeBadge(props: { type: string; class?: string }) {
  return (
    <span
      class={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border bg-surface-3 text-label-xs font-medium text-text-muted whitespace-nowrap",
        props.class,
      )}
      title={props.type}
    >
      <Car size={10} class="text-text-faint shrink-0" />
      {/* min-w-0 so the label can shrink and truncate when a caller caps the
          badge width (e.g. the sticky standings column); uncapped it sizes to
          content. */}
      <span class="truncate min-w-0">{props.type}</span>
    </span>
  );
}

function TournamentDetailPage() {
  const params = Route.useParams();
  const tournamentId = () => params().tournamentId;
  const { state } = useAuth();
  const qc = useQueryClient();
  const isAdmin = () => state.user?.role === "admin";
  const isJudge = () => state.user?.role === "judge" || isAdmin();

  const standings = createQuery(() => ({
    queryKey: [QueryKey.Tournaments, tournamentId(), "standings"],
    queryFn: () => tournamentsApi.standings(tournamentId()),
  }));

  const myProfile = createQuery(() => ({
    queryKey: [QueryKey.Profile, state.user?.id],
    queryFn: () => playersApi.get(state.user!.id),
    enabled: !!state.user?.id,
    staleTime: 60_000,
  }));

  const tournament = () => standings.data?.tournament;
  const participants = () => standings.data?.participants ?? [];
  const submittedRaces = () => standings.data?.races ?? [];
  const submittedRaceNumbers = createMemo(
    () => new Set(submittedRaces().map((r) => r.raceNumber)),
  );
  const completedRaces = () => submittedRaces().length;
  // Lowest race number that has not been submitted yet - robust to gaps left by
  // deleting a mistaken submission (don't assume contiguous submission order).
  const nextRace = createMemo(() => {
    const t = tournament();
    if (!t) return null;
    for (let n = 1; n <= t.racesCount; n++)
      if (!submittedRaceNumbers().has(n)) return n;
    return null;
  });
  const allRacesSubmitted = () => nextRace() === null;

  // When set, the submit panel edits an already-submitted race instead of the
  // next one. null → submit the next unsubmitted race.
  const [editingRace, setEditingRace] = createSignal<number | null>(null);
  const targetRace = () => editingRace() ?? nextRace();

  // The position a participant finished a given race in, or null if they have
  // no result for it (a DNS, or a not-yet-submitted race).
  const positionInRace = (
    p: TournamentStandings["participants"][number],
    raceNumber: number,
  ) => p.races.find((r) => r.raceNumber === raceNumber)?.position ?? null;

  // Order the drivers who raced a given race by finishing position (for
  // pre-filling the editor with what was actually submitted).
  const orderForRace = (raceNumber: number): string[] =>
    participants()
      .flatMap((p) => {
        const position = positionInRace(p, raceNumber);
        return position != null ? [{ userId: p.userId, position }] : [];
      })
      .sort((a, b) => a.position - b.position)
      .map((p) => p.userId);

  // The complement: participants with no result in the race, who reopen in the
  // editor's did-not-start group rather than at the bottom of the order.
  const dnsForRace = (raceNumber: number): string[] =>
    participants()
      .filter((p) => positionInRace(p, raceNumber) == null)
      .map((p) => p.userId);

  // Baseline total excludes the target race's existing points, so the editor's
  // projected "new total" doesn't double-count a race being re-submitted.
  const baselineParticipants = (raceNumber: number | null) =>
    participants().map((p) => {
      const existing =
        raceNumber == null
          ? 0
          : (p.races.find((r) => r.raceNumber === raceNumber)?.points ?? 0);
      return {
        userId: p.userId,
        totalPoints: p.totalPoints - existing,
        user: p.user,
      };
    });

  const isEligible = (t: Tournament): boolean => {
    const profile = myProfile.data;
    if (!profile) return true;
    return isTournamentEligible(profile, t);
  };

  const invalidateTournament = () => {
    return Promise.all([
      qc.invalidateQueries({ queryKey: [QueryKey.Tournaments, tournamentId()] }),
      qc.invalidateQueries({ queryKey: [QueryKey.Tournaments] }),
    ]);
  };

  const updateStatus = createMutation(() => ({
    mutationFn: (status: TournamentStatus) =>
      tournamentsApi.updateStatus(tournamentId(), status),
    onSuccess: invalidateTournament,
  }));

  const complete = createMutation(() => ({
    mutationFn: () => tournamentsApi.complete(tournamentId()),
    onSuccess: invalidateTournament,
  }));

  const deleteRace = createMutation(() => ({
    mutationFn: (raceNumber: number) =>
      tournamentsApi.deleteResults(tournamentId(), raceNumber),
    onSuccess: () => {
      setEditingRace(null);
      invalidateTournament();
    },
  }));

  const raceColumns = createMemo(() => {
    const t = tournament();
    if (!t) return [];
    return Array.from({ length: t.racesCount }, (_, i) => i + 1);
  });

  return (
    <PageContainer>
      <div class="flex items-center justify-between gap-2 mb-2">
        <div class="flex items-center gap-2">
          <Link
            to="/tournaments"
            search={{ tab: "active" }}
            class="flex items-center gap-1 text-sm text-text-faint hover:text-text-muted transition-colors"
          >
            <ChevronLeft size={14} />
            Tournaments
          </Link>
          <Show when={isAdmin() && tournament()?.status === "upcoming"}>
            <span class="text-sm text-text-faint">|</span>
            <span class="text-xs text-text-faint">
              Drag to reveal controls ↓
            </span>
          </Show>
        </div>
        <RulesDialog scope="tournaments" />
      </div>

      <Show when={standings.data} fallback={<HeroSkeleton />}>
        {(data) => {
          const t = () => data().tournament;
          // The drawer is the confirmation gate for the secondary controls
          // (edit, cancel), only meaningful while the tournament is still
          // upcoming. The forward status transitions live next to the status
          // badge instead, where their wide labels and errors have room.
          const drawer = children(() =>
            isAdmin() && t().status === "upcoming" ? (
              <div class="flex items-center justify-between gap-2 mb-2 flex-grow">
                <EditTournamentDialog
                  tournament={t()}
                  onSuccess={invalidateTournament}
                />
                <Button
                  size="sm"
                  variant="danger-outline"
                  onClick={() => updateStatus.mutate("cancelled")}
                  disabled={updateStatus.isPending}
                >
                  Cancel
                </Button>
              </div>
            ) : undefined,
          );
          return (
            <>
              {/* Hero: card | metadata | tracks (lg+) */}
              <div class="grid grid-cols-1 sm:grid-cols-[400px_1fr] lg:grid-cols-[440px_1fr_auto] gap-4 items-start">
                <div class="space-y-3">
                  <TournamentCard
                    tournament={{ ...t(), participants: participants() }}
                    userId={state.user?.id}
                    eligible={isEligible(t())}
                    interactive={false}
                    drawer={drawer()}
                  />
                </div>

                {/* Metadata panel */}
                <div class="space-y-3">
                  <div class="space-y-1.5">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        class={cn(
                          "inline-flex items-center px-2 py-0.5 rounded border text-label-xs font-semibold uppercase tracking-wider",
                          STATUS_STYLES[t().status as TournamentStatus],
                        )}
                      >
                        {t().status}
                      </span>
                      <Show when={isAdmin()}>
                        <Show when={t().status === "upcoming"}>
                          <Button
                            size="sm"
                            onClick={() => updateStatus.mutate("active")}
                            disabled={
                              updateStatus.isPending ||
                              !t().participants?.length
                            }
                          >
                            Start Tournament
                          </Button>
                        </Show>
                        <Show
                          when={t().status === "active" && allRacesSubmitted()}
                        >
                          <Button
                            size="sm"
                            onClick={() => complete.mutate()}
                            disabled={complete.isPending}
                          >
                            <Trophy size={12} />
                            {complete.isPending
                              ? "Completing..."
                              : "Complete Tournament"}
                          </Button>
                        </Show>
                        <Show when={t().status === "cancelled"}>
                          <Button
                            size="sm"
                            onClick={() => updateStatus.mutate("upcoming")}
                            disabled={updateStatus.isPending}
                          >
                            Reinstate
                          </Button>
                        </Show>
                      </Show>
                    </div>
                    <Show when={complete.error}>
                      <p class="text-xs text-red-400">
                        {complete.error?.message}
                      </p>
                    </Show>
                    <Show when={updateStatus.error}>
                      <p class="text-xs text-red-400">
                        {updateStatus.error?.message}
                      </p>
                    </Show>
                  </div>

                  <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm max-w-64">
                    <dt class="text-text-faint gap-1">Promotion</dt>
                    <dd
                      class={cn(
                        "font-semibold gap-1 inline-flex whitespace-nowrap items-center",
                        t().promotionMode === "showdown" || t().promotionMode === "super"
                          ? "text-gold"
                          : "text-text-base",
                      )}
                    >
                      <Show when={t().promotionMode === "showdown"}>
                        <Crown size={12} class="text-gold fill-gold min-w-3 -mt-0.5" />
                      </Show>
                      {PROMOTION_MODE_LABELS[t().promotionMode]}
                      <PromotionModeHelp />
                    </dd>

                    <dt class="text-text-faint">Races</dt>
                    <dd class="text-text-base font-semibold whitespace-nowrap">
                      {t().racesCount}
                      <Show
                        when={t().status === "active" && completedRaces() > 0}
                      >
                        <span class="text-text-faint font-normal ml-1">
                          ({completedRaces()} submitted)
                        </span>
                      </Show>
                    </dd>

                    <Show when={t().scheduledAt}>
                      <dt class="text-text-faint flex items-center gap-1">
                        <Calendar size={11} />
                        Scheduled
                      </dt>
                      <dd class="text-text-base font-semibold whitespace-nowrap">
                        {formatDateTime(t().scheduledAt!)}
                      </dd>
                    </Show>

                    <Show when={t().completedAt}>
                      <dt class="text-text-faint flex items-center gap-1">
                        <Flag size={11} />
                        Completed
                      </dt>
                      <dd class="text-text-base font-semibold whitespace-nowrap">
                        {formatDateTime(t().completedAt!)}
                      </dd>
                    </Show>
                  </dl>
                </div>

                {/* Track schedule — third hero column on desktop, stacks below on smaller screens */}
                <Show when={t().trackNames?.some(Boolean) || t().carTypes?.some(Boolean)}>
                  <div class="space-y-2 text-left lg:text-right sm:mr-3">
                    <h3 class="font-display text-xs font-semibold text-text-muted uppercase tracking-widest">
                      Tracks
                    </h3>
                    <ol class="flex flex-col gap-1">
                      <For
                        each={Array.from(
                          { length: t().racesCount },
                          (_, i) => i,
                        )}
                      >
                        {(i) => (
                          <li class="flex flex-col gap-0.5 items-start lg:items-end">
                            <div class="text-sm text-text-base flex gap-1.5 justify-start lg:justify-end">
                              <span class="text-text-faint tabular-nums">
                                {i + 1}.
                              </span>
                              {t().trackNames?.[i]?.trim() || (
                                <span class="text-text-faint italic">TBD</span>
                              )}
                            </div>
                            <div class="flex gap-1 items-center">
                              <Show when={carTypeFor(t().carTypes, i + 1)}>
                                {(ct) => <CarTypeBadge type={ct()} />}
                              </Show>
                              <Show when={multiplierFor(t().raceMultipliers, i + 1) > 1}>
                                <MultiplierBadge multiplier={multiplierFor(t().raceMultipliers, i + 1)} />
                              </Show>
                            </div>
                          </li>
                        )}
                      </For>
                    </ol>
                  </div>
                </Show>
              </div>

              {/* Race result submission / editing (judge/admin, active only) */}
              <Show
                when={
                  isJudge() && t().status === "active" && targetRace() != null
                }
              >
                <Show when={targetRace()} keyed>
                  {(rn) => (
                    <SubmitRacePanel
                      tournamentId={tournamentId()}
                      raceNumber={rn}
                      editing={editingRace() != null}
                      trackName={raceName(t().trackNames, rn)}
                      carType={carTypeFor(t().carTypes, rn)}
                      pointsScale={t().pointsScale}
                      multiplier={multiplierFor(t().raceMultipliers, rn)}
                      participants={baselineParticipants(rn)}
                      initialOrder={
                        editingRace() != null ? orderForRace(rn) : undefined
                      }
                      initialDns={
                        editingRace() != null ? dnsForRace(rn) : undefined
                      }
                      onCancel={
                        editingRace() != null
                          ? () => setEditingRace(null)
                          : undefined
                      }
                      onSuccess={() => {
                        setEditingRace(null);
                        qc.invalidateQueries({
                          queryKey: [QueryKey.Tournaments, tournamentId()],
                        });
                      }}
                    />
                  )}
                </Show>
              </Show>

              {/* Cancelled notice */}
              <Show when={t().status === "cancelled"}>
                <p class="text-sm text-text-faint py-4 text-center">
                  This tournament was cancelled.
                </p>
              </Show>

              {/* Standings / participant list */}
              <Show
                when={participants().length > 0}
                fallback={
                  <Show when={!(isAdmin() && t().status === "upcoming")}>
                    <p class="text-sm text-text-faint py-4 text-center">
                      No participants yet.
                    </p>
                  </Show>
                }
              >
                <Show
                  when={t().status !== "upcoming"}
                  fallback={
                    <section class="space-y-3">
                      <h2 class="font-display text-sm font-semibold text-text-muted uppercase tracking-widest">
                        Registered Drivers
                      </h2>
                      <Show
                        when={isAdmin()}
                        fallback={
                          <ParticipantList participants={participants()} />
                        }
                      >
                        <AdminParticipantManager
                          tournament={t()}
                          participants={participants()}
                          onSuccess={invalidateTournament}
                        />
                      </Show>
                    </section>
                  }
                >
                  <section class="space-y-3">
                    <h2 class="font-display text-sm font-semibold text-text-muted uppercase tracking-widest">
                      Standings
                    </h2>
                    <StandingsTable
                      participants={participants()}
                      raceColumns={raceColumns()}
                      trackNames={t().trackNames}
                      carTypes={t().carTypes}
                      raceMultipliers={t().raceMultipliers}
                      races={submittedRaces()}
                      pointsScale={t().pointsScale}
                      isCompleted={t().status === "completed"}
                      isShowdown={t().promotionMode === "showdown"}
                      canManage={isJudge() && t().status === "active"}
                      editingRace={editingRace()}
                      onEditRace={(n) =>
                        setEditingRace((cur) => (cur === n ? null : n))
                      }
                      onDeleteRace={(n) => deleteRace.mutate(n)}
                      deletePending={deleteRace.isPending}
                      deleteError={deleteRace.error?.message}
                    />
                  </section>
                </Show>
              </Show>

              {/* Admin participant manager when no participants yet */}
              <Show
                when={
                  isAdmin() &&
                  t().status === "upcoming" &&
                  participants().length === 0
                }
              >
                <section class="space-y-3">
                  <h2 class="font-display text-sm font-semibold text-text-muted uppercase tracking-widest">
                    Registered Drivers
                  </h2>
                  <AdminParticipantManager
                    tournament={t()}
                    participants={[]}
                    onSuccess={invalidateTournament}
                  />
                </section>
              </Show>

              {/* Promotion projection: the field's finishing brackets and the tier
                  climb each carries. Hidden once completed, when the standings
                  show the real promotions instead. A roster of at least two is a
                  real field; before that the projection falls back to the
                  tournament's max (or the representative sample). */}
              <Show when={t().status === "upcoming" || t().status === "active"}>
                <section class="space-y-3">
                  <h2 class="font-display text-sm font-semibold text-text-muted uppercase tracking-widest">
                    Promotion grid
                  </h2>
                  <PromotionPreview
                    mode={t().promotionMode}
                    eligibleTiers={t().eligibleTiers}
                    maxDrivers={
                      participants().length >= 2
                        ? participants().length
                        : t().maxParticipants ?? undefined
                    }
                    caption={
                      participants().length >= 2
                        ? `The current field of ${participants().length} by finishing position.`
                        : undefined
                    }
                  />
                </section>
              </Show>
            </>
          );
        }}
      </Show>
    </PageContainer>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ParticipantList(props: {
  participants: Array<{
    userId: string;
    user?: {
      displayName: string;
      avatarUrl?: string | null;
      username?: string;
      gamertag?: string | null;
    };
  }>;
}) {
  return (
    <div class="flex flex-wrap gap-3">
      <For each={props.participants}>
        {(p) => (
          <div class="flex items-center gap-2 bg-surface-2 rounded-lg px-3 py-2 border border-border">
            <Avatar
              src={p.user?.avatarUrl ?? undefined}
              fallback={p.user?.displayName ?? "?"}
              size="sm"
            />
            <div class="leading-tight min-w-0">
              <Show
                when={p.user?.username}
                fallback={
                  <span class="block text-sm text-text-base font-semibold truncate">
                    {p.user?.displayName ?? p.userId}
                  </span>
                }
              >
                {(username) => (
                  <Link
                    to="/profile/$username"
                    params={{ username: username() }}
                    search={{ tab: undefined }}
                    class="block text-sm text-text-base font-semibold truncate hover:text-brand transition-colors"
                  >
                    {p.user?.displayName ?? p.userId}
                  </Link>
                )}
              </Show>
              <Show when={p.user}>{(u) => <GamertagFull user={u()} />}</Show>
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

function AdminParticipantManager(props: {
  tournament: {
    id: string;
    raceType: RaceType;
    carClass: CarClass;
    eligibleTiers: Tier[];
    maxParticipants: number | null;
  };
  participants: Array<{
    userId: string;
    user?: { displayName: string; avatarUrl?: string | null };
  }>;
  onSuccess: () => void | Promise<unknown>;
}) {
  const categoryLabel = () => {
    const rt = props.tournament.raceType.replace("_", " ");
    return `${rt.charAt(0).toUpperCase()}${rt.slice(1)} ${props.tournament.carClass.toUpperCase()}`;
  };

  return (
    <div class="space-y-3">
      <Show
        when={props.participants.length > 0}
        fallback={
          <p class="text-xs text-text-faint">No drivers registered yet.</p>
        }
      >
        <ParticipantList participants={props.participants} />
      </Show>
      <EligibleMembersDialog
        raceType={props.tournament.raceType}
        carClass={props.tournament.carClass}
        eligibleTiers={props.tournament.eligibleTiers}
        categoryLabel={categoryLabel()}
        tournamentId={props.tournament.id}
        participants={props.participants}
        canManage
        maxParticipants={props.tournament.maxParticipants}
        onSuccess={props.onSuccess}
        triggerLabel="Manage roster"
        triggerVariant="outline"
      />
    </div>
  );
}

function EditTournamentDialog(props: {
  tournament: Tournament;
  onSuccess: () => void;
}) {
  const [open, setOpen] = createSignal(false);

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <DialogTrigger as={Button} size="sm" variant="ghost">
        <Pencil size={12} /> Edit
      </DialogTrigger>
      <DialogContent title="Edit Tournament" class="sm:max-w-5xl">
        <Show when={open()}>
          <EditTournamentForm
            tournament={props.tournament}
            onSuccess={() => {
              setOpen(false);
              props.onSuccess();
            }}
            onCancel={() => setOpen(false)}
          />
        </Show>
      </DialogContent>
    </Dialog>
  );
}

function EditTournamentForm(props: {
  tournament: Tournament;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const t = props.tournament;
  const [name, setName] = createSignal(t.name);
  const [eligibleTiers, setEligibleTiers] = createSignal<Tier[]>([
    ...t.eligibleTiers,
  ]);
  const toggleTier = (tier: Tier) =>
    setEligibleTiers((prev) =>
      prev.includes(tier) ? prev.filter((x) => x !== tier) : [...prev, tier],
    );
  const [raceType, setRaceType] = createSignal<RaceType>(t.raceType);
  const [carClass, setCarClass] = createSignal<CarClass>(t.carClass);
  const { tracks, setTrackName, setCarType, setMultiplier, setAllCarTypes, addTrack, removeTrack } =
    createTrackList(tracksFromArrays(t.trackNames, t.carTypes, t.raceMultipliers, t.trackNames?.length || t.racesCount));
  const [promotionMode, setPromotionMode] = createSignal<PromotionMode>(
    t.promotionMode,
  );
  const [maxParticipants, setMaxParticipants] = createSignal(
    t.maxParticipants?.toString() ?? "",
  );
  const [scheduledAt, setScheduledAt] = createSignal(
    t.scheduledAt ? new Date(t.scheduledAt).toLocaleString('sv-u-ca-iso8601').replace(' ', 'T') : "",
  );

  const update = createMutation(() => ({
    mutationFn: () => {
      return tournamentsApi.update(t.id, {
        name: name(),
        eligibleTiers: eligibleTiers(),
        raceType: raceType(),
        carClass: carClass(),
        racesCount: tracks().length,
        ...tracksToArrays(tracks()),
        promotionMode: promotionMode(),
        maxParticipants: maxParticipants()
          ? parseInt(maxParticipants(), 10)
          : null,
        scheduledAt: scheduledAt()
          ? new Date(scheduledAt()).toISOString()
          : null,
      });
    },
    onSuccess: props.onSuccess,
  }));

  return (
    <>
      <DialogBody>
        <TournamentFormFields
          name={name()}
          onName={setName}
          eligibleTiers={eligibleTiers()}
          onToggleTier={toggleTier}
          raceType={raceType()}
          onRaceType={setRaceType}
          carClass={carClass()}
          onCarClass={setCarClass}
          tracks={tracks()}
          onTrackName={setTrackName}
          onCarType={setCarType}
          onMultiplier={setMultiplier}
          onAddTrack={addTrack}
          onRemoveTrack={removeTrack}
          onAllCarTypes={setAllCarTypes}
          promotionMode={promotionMode()}
          onPromotionMode={setPromotionMode}
          maxParticipants={maxParticipants()}
          onMaxParticipants={setMaxParticipants}
          scheduledAt={scheduledAt()}
          onScheduledAt={setScheduledAt}
        />
      </DialogBody>
      <Show when={update.error}>
        <p class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mt-4">
          {update.error?.message}
        </p>
      </Show>
      <div class="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => update.mutate()}
          disabled={!name().trim() || update.isPending}
        >
          {update.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </>
  );
}

function StandingsTable(props: {
  participants: Array<{
    userId: string;
    placement: number | null;
    totalPoints: number;
    promoted: boolean;
    user?: { displayName: string; avatarUrl?: string | null };
    races: Array<{
      raceNumber: number;
      position: number | null;
      points: number | null;
    }>;
  }>;
  raceColumns: number[];
  trackNames: string[] | null | undefined;
  carTypes: string[] | null | undefined;
  raceMultipliers: number[] | null | undefined;
  races: Array<{
    raceNumber: number;
    submittedAt: string | null;
    submittedBy: { id: string; displayName: string } | null;
  }>;
  pointsScale: number[];
  isCompleted: boolean;
  isShowdown: boolean;
  canManage: boolean;
  editingRace: number | null;
  onEditRace: (n: number) => void;
  onDeleteRace: (n: number) => void;
  deletePending: boolean;
  deleteError?: string;
}) {
  const [confirming, setConfirming] = createSignal<number | null>(null);
  const raceMeta = (n: number) => props.races.find((r) => r.raceNumber === n);
  const submitted = (n: number) => !!raceMeta(n);
  const result = (p: (typeof props.participants)[number], n: number) =>
    p.races.find((r) => r.raceNumber === n);
  // First column (race labels) and the trailing actions column are sticky so they
  // stay put while the driver columns scroll horizontally.
  const stickyLeft = "sticky left-0 z-10";

  return (
    <div class="space-y-2">
      {/* Bordered box clips the rounded corners (overflow-hidden); the inner div
          owns the horizontal scroll so sticky cells can't bleed past the radius. */}
      <div class="rounded-xl border border-border overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr class="bg-surface-2">
                <th
                  class={cn(
                    "text-left align-bottom px-3 py-2.5 text-xs font-semibold text-text-muted uppercase tracking-wider bg-surface-2 border-b border-border min-w-[120px]",
                    stickyLeft,
                  )}
                >
                  Race
                </th>
                <For each={props.participants}>
                  {(p) => (
                    <th
                      class={cn(
                        "px-3 py-2.5 align-bottom border-b border-border min-w-[76px]",
                        p.promoted &&
                          (props.isShowdown
                            ? "bg-gold/10"
                            : "bg-green-500/5"),
                      )}
                    >
                      {/* Name is the last (bottom) element so every column's name —
                        and the "Race" label - shares one baseline; medal and the
                        Promoted badge stack upward and don't shift it. */}
                      <div class="flex flex-col items-center gap-1.5">
                        <Show when={p.promoted}>
                          <span
                            class={cn(
                              "text-label-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border",
                              props.isShowdown
                                ? "bg-gold/15 text-gold border-gold/30"
                                : "bg-green-500/15 text-green-400 border-green-500/20",
                            )}
                          >
                            {props.isShowdown ? (
                              <>
                                <Crown size={14} class="inline text-gold fill-gold -mt-0.5" /> Winner
                              </>
                            ) : (
                              "Promoted"
                            )}
                          </span>
                        </Show>
                        <Show when={props.isCompleted && p.placement != null}>
                          <Switch
                            fallback={
                              <span class="font-mono text-xs text-text-faint">
                                #{p.placement}
                              </span>
                            }
                          >
                            <Match when={p.placement === 1}>
                              <Trophy size={14} class="text-yellow-400" />
                            </Match>
                            <Match when={p.placement === 2}>
                              <Medal size={14} class="text-slate-300" />
                            </Match>
                            <Match when={p.placement === 3}>
                              <Medal size={14} class="text-amber-700" />
                            </Match>
                          </Switch>
                        </Show>
                        <Avatar
                          src={p.user?.avatarUrl ?? undefined}
                          fallback={p.user?.displayName ?? "?"}
                          size="sm"
                        />
                        <span class="font-semibold text-text-base truncate max-w-[88px] text-xs">
                          {p.user?.displayName ?? p.userId}
                        </span>
                      </div>
                    </th>
                  )}
                </For>
                <Show when={props.canManage}>
                  <th
                    class={cn(
                      "px-2 py-2 border-b border-border bg-surface-2 w-px",
                      "sticky right-0 z-10",
                    )}
                  />
                </Show>
              </tr>
            </thead>
            <tbody>
              <For each={props.raceColumns}>
                {(n) => {
                  const meta = () => raceMeta(n);
                  const editing = () => props.editingRace === n;
                  return (
                    <tr
                      class={cn(
                        "transition-colors",
                        editing() ? "bg-brand/5" : "bg-surface-1",
                      )}
                    >
                      <td
                        class={cn(
                          "px-3 py-2.5 border-b border-border align-top",
                          editing() ? "bg-brand/5" : "bg-surface-1",
                          stickyLeft,
                        )}
                      >
                        <div class="font-semibold text-text-base whitespace-nowrap">
                          {raceName(props.trackNames, n)}
                        </div>
                        <Show when={carTypeFor(props.carTypes, n)}>
                          {(ct) => <CarTypeBadge type={ct()} class="mt-0.5 max-w-[160px]" />}
                        </Show>
                        <Show when={multiplierFor(props.raceMultipliers, n) > 1}>
                          <MultiplierBadge multiplier={multiplierFor(props.raceMultipliers, n)} class="mt-0.5" />
                        </Show>
                        <Show when={meta()?.submittedBy}>
                          {(by) => (
                            <div class="text-label-xs text-text-faint truncate max-w-[160px]">
                              by {by().displayName}
                              {meta()?.submittedAt
                                ? ` · ${formatDateTime(meta()!.submittedAt!)}`
                                : ""}
                            </div>
                          )}
                        </Show>
                      </td>
                      <For each={props.participants}>
                        {(p) => {
                          const r = result(p, n);
                          return (
                            <td
                              class={cn(
                                "px-3 py-2.5 text-center border-b border-border tabular-nums",
                                p.promoted &&
                                  (props.isShowdown
                                    ? "bg-gold/10"
                                    : "bg-green-500/5"),
                              )}
                            >
                              <Show
                                when={r && r.position != null}
                                fallback={
                                  <span class="text-text-faint">—</span>
                                }
                              >
                                <span class="text-text-base font-semibold">
                                  {r!.position}
                                </span>
                                <span class="text-text-faint text-xs ml-1">
                                  ({r!.points})
                                </span>
                              </Show>
                            </td>
                          );
                        }}
                      </For>
                      <Show when={props.canManage}>
                        <td
                          class={cn(
                            "px-2 py-2.5 border-b border-border sticky right-0 z-10",
                            editing() ? "bg-brand/5" : "bg-surface-1",
                          )}
                        >
                          <Show
                            when={submitted(n)}
                            fallback={
                              <span class="text-text-faint text-xs">—</span>
                            }
                          >
                            <div class="flex items-center justify-end gap-1 -my-2">
                              <Show
                                when={confirming() === n}
                                fallback={
                                  <>
                                    <Button
                                      size="sm"
                                      variant={editing() ? "default" : "ghost"}
                                      onClick={() => props.onEditRace(n)}
                                    >
                                      <Pencil size={12} />
                                    </Button>
                                    <button
                                      type="button"
                                      class="pressable text-text-faint hover:text-red-400 p-1.5"
                                      onClick={() => setConfirming(n)}
                                      aria-label={`Delete race ${n} results`}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </>
                                }
                              >
                                <Button
                                  size="sm"
                                  variant="danger-outline"
                                  onClick={() => {
                                    props.onDeleteRace(n);
                                    setConfirming(null);
                                  }}
                                  disabled={props.deletePending}
                                >
                                  Delete
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setConfirming(null)}
                                >
                                  Keep
                                </Button>
                              </Show>
                            </div>
                          </Show>
                        </td>
                      </Show>
                    </tr>
                  );
                }}
              </For>
            </tbody>
            <tfoot>
              <tr class="bg-surface-2 font-bold">
                <td
                  class={cn(
                    "px-3 py-2.5 text-text-muted uppercase tracking-wider text-xs bg-surface-2",
                    stickyLeft,
                  )}
                >
                  Total
                  <PointsScaleHelp scale={props.pointsScale} />
                </td>
                <For each={props.participants}>
                  {(p) => (
                    <td
                      class={cn(
                        "px-3 py-2.5 text-center text-text-base tabular-nums",
                        p.promoted &&
                          (props.isShowdown
                            ? "bg-gold/10 text-gold"
                            : "bg-green-500/10 text-green-300"),
                      )}
                    >
                      {p.totalPoints}
                    </td>
                  )}
                </For>
                <Show when={props.canManage}>
                  <td class="bg-surface-2 sticky right-0 z-10" />
                </Show>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <Show when={props.deleteError}>
        <p class="text-xs text-red-400">{props.deleteError}</p>
      </Show>
    </div>
  );
}

type SubmitRow = {
  uid: string;
  p: {
    userId: string;
    totalPoints: number;
    user?: {
      displayName: string;
      gamertag?: string | null;
      avatarUrl?: string | null;
    };
  };
  pos: number;
  racePts: number;
  newTotal: number;
};

function SortableRow(props: { row: SubmitRow; rank: number; onDns: () => void }) {
  const sortable = createSortable(props.row.uid);
  return (
    <tr
      ref={sortable.ref}
      style={transformStyle(sortable.transform)}
      {...sortable.dragActivators}
      class={cn(
        "border-b border-border last:border-0 cursor-grab active:cursor-grabbing",
        sortable.isActiveDraggable
          ? "opacity-30"
          : "transition-transform duration-150",
      )}
    >
      <td class="px-3 py-2.5 text-text-faint font-mono text-xs">
        {props.row.pos}
      </td>
      <td class="px-3 py-2.5">
        <div class="flex items-center gap-2">
          <GripVertical size={14} class="text-text-faint shrink-0" />
          <Avatar
            src={props.row.p.user?.avatarUrl ?? undefined}
            fallback={props.row.p.user?.displayName ?? "?"}
            size="sm"
          />
          <div class="flex min-w-0 gap-2">
            <span class="font-semibold text-text-base truncate max-w-[120px]">
              {props.row.p.user?.displayName ?? props.row.uid}
            </span>
            <Show when={props.row.p.user}>
              {(u) => <GamertagFull user={u()} copyable={false} />}
            </Show>
          </div>
        </div>
      </td>
      <td class="px-3 py-2.5 text-right font-semibold text-brand tabular-nums">
        +{props.row.racePts}
      </td>
      <td class="px-3 py-2.5 text-right font-bold text-text-base tabular-nums">
        {props.row.newTotal}
      </td>
      <td class="px-3 py-2.5 text-right text-text-muted tabular-nums">
        #{props.rank}
      </td>
      <td class="px-2 py-2.5 text-right w-px">
        {/* pointerdown must not reach the row, where it would arm the hold-to-drag sensor. */}
        <button
          type="button"
          class="pressable text-text-faint hover:text-red-400"
          title="Did not start — exclude from this race"
          aria-label="Mark as did not start"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => props.onDns()}
        >
          <UserX size={14} />
        </button>
      </td>
    </tr>
  );
}

function HoldPointerSensor() {
  const [
    state,
    {
      addSensor,
      removeSensor,
      sensorStart,
      sensorMove,
      sensorEnd,
      dragStart,
      dragEnd,
    },
  ] = useDragDropContext()!;

  const ID = "hold-pointer";
  const HOLD_MS = 200;
  const CANCEL_PX = 8;
  const MOUSE_ACTIVATE_PX = 4;

  let timer: ReturnType<typeof setTimeout> | undefined;
  let active = false;
  let isTouch = false;
  let ox = 0,
    oy = 0;
  let pendingId: string | number | null = null;

  const isActiveSensor = () => state.active.sensorId === ID;

  const activate = () => {
    if (pendingId === null) return;
    navigator.vibrate?.(10);
    sensorStart(ID, { x: ox, y: oy });
    dragStart(pendingId);
    active = true;
    pendingId = null;
  };

  const cleanup = () => {
    clearTimeout(timer);
    timer = undefined;
    pendingId = null;
    active = false;
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onUp);
  };

  const onMove = (e: PointerEvent) => {
    if (!active) {
      const dx = e.clientX - ox,
        dy = e.clientY - oy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (isTouch) {
        if (dist > CANCEL_PX) cleanup();
      } else {
        if (dist > MOUSE_ACTIVATE_PX) {
          clearTimeout(timer);
          timer = undefined;
          activate();
        }
      }
      return;
    }
    if (isActiveSensor()) {
      e.preventDefault();
      sensorMove({ x: e.clientX, y: e.clientY });
    }
  };

  const onUp = (e: PointerEvent) => {
    if (isActiveSensor()) {
      e.preventDefault();
      dragEnd();
      sensorEnd();
    }
    cleanup();
  };

  const attach = (e: PointerEvent, draggableId: string | number) => {
    if (e.button !== 0) return;
    ox = e.clientX;
    oy = e.clientY;
    pendingId = draggableId;
    isTouch = e.pointerType !== "mouse";
    active = false;
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp, { once: true });
    timer = setTimeout(activate, isTouch ? HOLD_MS : 250);
  };

  onMount(() => addSensor({ id: ID, activators: { pointerdown: attach } }));
  onCleanup(() => {
    cleanup();
    removeSensor(ID);
  });

  return <></>;
}

function SubmitRacePanel(props: {
  tournamentId: string;
  raceNumber: number;
  trackName: string;
  carType?: string;
  pointsScale: number[];
  multiplier: number;
  editing?: boolean;
  initialOrder?: string[];
  // Drivers who sat this race out (a did-not-start), excluded from the
  // submission so they score nothing for it. Pre-filled when editing a race
  // that already has absentees.
  initialDns?: string[];
  participants: Array<{
    userId: string;
    totalPoints: number;
    user?: {
      displayName: string;
      gamertag?: string | null;
      avatarUrl?: string | null;
    };
  }>;
  onSuccess: () => void;
  onCancel?: () => void;
}) {
  const [order, setOrder] = createSignal(
    props.initialOrder ?? props.participants.map((p) => p.userId),
  );
  const [dns, setDns] = createSignal<string[]>(props.initialDns ?? []);

  const markDns = (uid: string) => {
    setOrder((prev) => prev.filter((id) => id !== uid));
    setDns((prev) => [...prev, uid]);
  };
  // A restored driver re-enters at the bottom of the order; the organizer drags
  // them into place from there.
  const restore = (uid: string) => {
    setDns((prev) => prev.filter((id) => id !== uid));
    setOrder((prev) => [...prev, uid]);
  };

  const byId = createMemo(
    () => new Map(props.participants.map((p) => [p.userId, p])),
  );

  const rows = createMemo(() =>
    order().map((uid, i) => {
      const p = byId().get(uid)!;
      const racePts = (props.pointsScale[i] ?? 0) * props.multiplier;
      return {
        uid,
        p,
        pos: i + 1,
        racePts,
        newTotal: p.totalPoints + racePts,
      } satisfies SubmitRow;
    }),
  );

  // Projected standings include the did-not-start drivers at their baseline
  // totals, so the Standing column ranks against the whole field.
  const projectedRank = createMemo(() => {
    const totals = [
      ...rows().map((r) => ({ uid: r.uid, total: r.newTotal })),
      ...dns().map((uid) => ({ uid, total: byId().get(uid)!.totalPoints })),
    ].sort((a, b) => b.total - a.total);
    return new Map(totals.map((r, i) => [r.uid, i + 1]));
  });

  const onDragEnd: DragEventHandler = ({ draggable, droppable }) => {
    if (!droppable) return;
    const from = order().indexOf(String(draggable.id));
    const to = order().indexOf(String(droppable.id));
    if (from !== to) {
      setOrder((prev) => {
        const next = [...prev];
        next.splice(to, 0, ...next.splice(from, 1));
        return next;
      });
    }
  };

  const submit = createMutation(() => ({
    mutationFn: () => {
      const results = order().map((uid, i) => ({
        userId: uid,
        position: i + 1,
      }));
      return tournamentsApi.submitResults(
        props.tournamentId,
        props.raceNumber,
        { results },
      );
    },
    onSuccess: props.onSuccess,
  }));

  return (
    <section class="space-y-3 p-4 rounded-xl bg-surface-2 border border-border">
      <div class="flex items-center gap-2 flex-wrap">
        <h3 class="font-display text-sm font-semibold uppercase tracking-widest text-brand">
          {props.editing ? "Edit" : "Submit"} - {props.trackName}
        </h3>
        <Show when={props.carType}>
          {(ct) => <CarTypeBadge type={ct()} />}
        </Show>
        <Show when={props.multiplier > 1}>
          <MultiplierBadge multiplier={props.multiplier} />
        </Show>
      </div>
      <DragDropProvider onDragEnd={onDragEnd} collisionDetector={closestCenter}>
        <HoldPointerSensor />
        <div class="overflow-x-auto rounded-lg border border-border select-none">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-surface-3">
                <th class="px-3 py-2 text-left text-xs font-semibold text-text-muted uppercase tracking-wider w-8">
                  #
                </th>
                <th class="px-3 py-2 text-left text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Driver
                </th>
                <th class="px-3 py-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Race Pts
                </th>
                <th class="px-3 py-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Total
                </th>
                <th class="px-3 py-2 text-right text-xs font-semibold text-text-muted uppercase tracking-wider">
                  Standing
                </th>
                <th class="px-2 py-2 w-px" />
              </tr>
            </thead>
            <SortableProvider ids={order()}>
              <tbody>
                <For each={rows()}>
                  {(row) => (
                    <SortableRow
                      row={row}
                      rank={projectedRank().get(row.uid)!}
                      onDns={() => markDns(row.uid)}
                    />
                  )}
                </For>
              </tbody>
            </SortableProvider>
          </table>
        </div>
      </DragDropProvider>
      <Show when={dns().length > 0}>
        <div class="space-y-1.5">
          <p class="text-xs font-semibold text-text-muted uppercase tracking-wider">
            Did not start <span class="normal-case text-text-faint font-normal">— excluded from this race, scores nothing</span>
          </p>
          <div class="flex flex-wrap gap-1.5">
            <For each={dns()}>
              {(uid) => {
                const p = byId().get(uid)!;
                return (
                  <span class="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-1 rounded-lg border border-border bg-surface-3 text-sm">
                    <Avatar
                      src={p.user?.avatarUrl ?? undefined}
                      fallback={p.user?.displayName ?? "?"}
                      size="sm"
                    />
                    <span class="text-text-muted line-through">{p.user?.displayName ?? uid}</span>
                    <button
                      type="button"
                      class="pressable text-text-faint hover:text-text-base"
                      title="Restore to the running order"
                      aria-label="Restore to the running order"
                      onClick={() => restore(uid)}
                    >
                      <Undo2 size={14} />
                    </button>
                  </span>
                );
              }}
            </For>
          </div>
        </div>
      </Show>
      <Show when={order().length === 0}>
        <p class="text-xs text-text-faint">At least one driver must have raced.</p>
      </Show>
      <Show when={submit.error}>
        <p class="text-xs text-red-400">{submit.error?.message}</p>
      </Show>
      <div class="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => submit.mutate()}
          disabled={submit.isPending || order().length === 0}
        >
          {submit.isPending
            ? "Saving..."
            : props.editing
              ? `Update Race ${props.raceNumber}`
              : `Submit Race ${props.raceNumber} Results`}
        </Button>
        <Show when={props.onCancel}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => props.onCancel!()}
            disabled={submit.isPending}
          >
            Cancel
          </Button>
        </Show>
      </div>
    </section>
  );
}

function HeroSkeleton() {
  return (
    <div class="space-y-4">
      <div class="h-40 rounded-xl bg-surface-2 animate-pulse" />
      <div class="h-48 rounded-xl bg-surface-2 animate-pulse" />
    </div>
  );
}
